import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Camera, ChevronDown, Download, History, LayoutDashboard, RotateCcw, Save, UserPlus, User, Table, ChevronLeft, ChevronRight, PersonStanding } from 'lucide-react';
import { ClientsOnlyAppWall, useClientsOnlySession } from '@/components/authnz/ClientsOnlyAppWall';
import PageLayout from '@/components/shared/PageLayout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SelectClientDialog } from '@/components/select-client-dialog';
import { useClient } from '@/context/client-context';
import { getPassiveTestData, getActiveTestData, getLayoutOrder, getVisibleCourseTestKeys } from '@/components/sop-intake/constants';
import { resolveAssessmentTestMode } from '@/components/sop-intake/course-playlist-config';
import {
  countVisibleCompletedSides,
  countVisibleTotalSides,
  toVisibleTestKeySet,
} from '@/components/sop-intake/sop-analyzer/visible-battery';
import TestInputGrid from '@/components/sop-intake/TestInputGrid';
import VisioMeter2 from '../Visual-GON/VisioMeter2';
import { SettingsProvider as VGonSettingsProvider } from '../Visual-GON/SettingsContext';
import type { Assessment } from '@/lib/types';
import { getClientAssessments, updateAssessment } from '@/components/sop-intake/services/assessments-api';
import { useSopAnalyzer } from '@/hooks/use-sop-analyzer';
import SopAnalysisPanel from '@/components/sop-intake/sop-analyzer/SopAnalysisPanel';
import { cn, formatDateTimeLocale, pickLatestRecordBySortDate, sortRecordsBySortDateDesc } from '@/lib/utils';
import { useSettings } from '@/components/settings/lib/settings-context';
import { ToolbarButton } from '@/components/shared/ToolbarButton';
import {
  useSopSheetImport,
  SopSheetImportPasteListener,
  SopSheetImportHiddenFileInput,
  SopSheetImportButton,
} from '@/components/sop-intake/SopSheetImageImport';
import type { RuleDocVersion } from '@/lib/posture-analysis/rule-params';
import {
  getClientPostureAnalyses,
  getPostureAnalysis,
} from '@/components/posture-analysis/services/posture-analyses-api';
import { classifyPosture } from "@/components/posture-analysis/lib/analysis";
import { inferSagittalInputFromCaptured } from '@/lib/posture-analysis/posture-pic-evaluation/strategies/posture-strategy-context';
import { resolvePostureOrientationStrategyId } from '@/lib/posture-analysis/posture-pic-evaluation/strategies/posture-orientation-strategy-id';
import { ClientPostureOverview } from "@/components/posture-analysis/ClientPostureOverview";
import type { PostureAnalysis } from "@/lib/types";
import logger from '@/lib/logging';
import { computeSopPosture } from '@/lib/posture-analysis/sop-evaluation';

interface SopAssessmentPageProps {
  mode: 'new' | 'edit';
  initialAssessmentData?: Assessment | null;
}

export default function SopAssessmentPage({ mode, initialAssessmentData = null }: SopAssessmentPageProps) {
  const { client, setClient, isClientLoading } = useClient();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const hasAutoRedirectedToLatestRef = useRef(false);
  const ruleDocVersion: RuleDocVersion = settings.useLegacySopAssessment ? 'legacy' : 'current';
  const [testMode, setTestMode] = useState<'passive' | 'active'>(() =>
    resolveAssessmentTestMode((initialAssessmentData ?? {}) as Record<string, unknown>)
  );
  const [showOptionalAssessments, setShowOptionalAssessments] = useState(false);
  const passiveTestData = useMemo(() => getPassiveTestData(ruleDocVersion), [ruleDocVersion]);
  const activeTestData = useMemo(() => getActiveTestData(ruleDocVersion), [ruleDocVersion]);
  const layoutOrder = useMemo(
    () => getLayoutOrder(ruleDocVersion, testMode),
    [ruleDocVersion, testMode]
  );

  const editMode = mode === 'edit';

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isVGonOpen, setIsVGonOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState<{ testKey: string; side: 'L' | 'R' } | null>(null);
  const [isInsightsOpen, setIsInsightsOpen] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const clientsOnly = useClientsOnlySession();
  const [insightsTab, setInsightsTab] = useState<'body-map' | 'sop-table'>('body-map');
  const [postureSidebarCollapsed, setPostureSidebarCollapsed] = useState(false);
  const currentTestDataMap = useMemo(
    () => (testMode === 'passive' ? passiveTestData : activeTestData),
    [activeTestData, passiveTestData, testMode]
  );
  const [priorAssessments, setPriorAssessments] = useState<
    Array<{ id: string; label: string; createdAt?: Date | null }>
  >([]);
  const [latestPostureAnalysis, setLatestPostureAnalysis] = useState<PostureAnalysis | null>(null);
  const [postureClassification, setPostureClassification] = useState<any>(null);

  const {
    formValues,
    setFormValues,
    handleInputChange,
    analysis,
    isAnalyzing,
    analysisError,
    handleSave,
    handleExportPdf,
    resetForm,
    saveStatus,
  } = useSopAnalyzer(client, setClient, initialAssessmentData);

  useEffect(() => {
    if (initialAssessmentData) {
      setTestMode(resolveAssessmentTestMode(initialAssessmentData as Record<string, unknown>));
    }
  }, [initialAssessmentData]);

  const handleTestModeChange = (mode: 'passive' | 'active') => {
    setTestMode(mode);
    handleInputChange('testMode', mode);
  };

  const sheetImport = useSopSheetImport({
    experimentalFeaturesEnabled: !!settings.experimentalFeaturesEnabled,
    formValues,
    onInputChange: handleInputChange,
    testMode,
    ruleDocVersion,
  });

  const visibleTestKeys = useMemo(
    () =>
      toVisibleTestKeySet(
        getVisibleCourseTestKeys(ruleDocVersion, {
          mode: testMode,
          footBatteryEnabled: false,
          showOptionalAssessments,
        })
      ),
    [ruleDocVersion, showOptionalAssessments, testMode]
  );

  const completedTests = useMemo(
    () => countVisibleCompletedSides(formValues, visibleTestKeys, currentTestDataMap),
    [currentTestDataMap, formValues, visibleTestKeys]
  );
  const totalTests = useMemo(
    () => countVisibleTotalSides(visibleTestKeys, currentTestDataMap),
    [currentTestDataMap, visibleTestKeys]
  );
  const currentAssessmentLabel = initialAssessmentData
    ? formatDateTimeLocale(
        (initialAssessmentData as any).date ?? (initialAssessmentData as any).createdAt
      )
    : null;

  const assessmentsForDropdown = useMemo(() => {
    if (initialAssessmentData && currentAssessmentLabel) {
      return [
        { id: initialAssessmentData.id, label: currentAssessmentLabel },
        ...priorAssessments,
      ];
    }
    return priorAssessments;
  }, [initialAssessmentData, currentAssessmentLabel, priorAssessments]);

  useEffect(() => {
    hasAutoRedirectedToLatestRef.current = false;
  }, [client?.id]);

  useEffect(() => {
    const fetchPriorAssessments = async () => {
      if (!client?.id) return;
      const assessments = await getClientAssessments(client.id);
      const filtered = initialAssessmentData
        ? assessments.filter((assessment) => assessment.id !== initialAssessmentData.id)
        : assessments;

      const formatted = sortRecordsBySortDateDesc(filtered).map((assessment) => {
        const raw = (assessment as any).date ?? (assessment as any).createdAt;
        const date =
          raw && typeof raw.toDate === 'function' ? raw.toDate() : raw instanceof Date ? raw : new Date(raw as any);
        return {
          id: assessment.id,
          label: formatDateTimeLocale(date),
          createdAt: date,
        };
      });
      setPriorAssessments(formatted);
    };
    const fetchLatestPosture = async () => {
      if (!client?.id) return;
      const analyses = await getClientPostureAnalyses(client.id);
      const latest = pickLatestRecordBySortDate(analyses);
      if (latest) {
        const analysisData = await getPostureAnalysis(client.id, latest.id);
        if (analysisData) {
          setLatestPostureAnalysis(analysisData as PostureAnalysis);
        }
      }
    };
    fetchPriorAssessments();
    fetchLatestPosture();
  }, [client?.id, initialAssessmentData]);

  useEffect(() => {
    if (
      editMode ||
      initialAssessmentData ||
      !client?.id ||
      hasAutoRedirectedToLatestRef.current ||
      location.pathname.endsWith('/assessment/new')
    ) {
      return;
    }

    let cancelled = false;

    const redirectToLatestAssessment = async () => {
      const assessments = await getClientAssessments(client.id);
      const latest = pickLatestRecordBySortDate(assessments);
      if (cancelled || !latest) return;

      hasAutoRedirectedToLatestRef.current = true;
      navigate(`/clients/${client.id}/assessment/${encodeURIComponent(latest.id)}`, { replace: true });
    };

    void redirectToLatestAssessment();

    return () => {
      cancelled = true;
    };
  }, [client?.id, editMode, initialAssessmentData, location.pathname, navigate]);

  useEffect(() => {
    if (!latestPostureAnalysis?.captured || !latestPostureAnalysis?.analysis) {
        setPostureClassification(null);
        return;
    }

    const { sagittal } = inferSagittalInputFromCaptured(latestPostureAnalysis.captured);
    const strategyId = resolvePostureOrientationStrategyId(
      settings.postureOrientationStrategyId,
      settings.experimentalFeaturesEnabled === true,
      true
    );
    
    const sopCompletionRatio = totalTests > 0 ? Math.min(1, completedTests / totalTests) : 0;
    const sopTablePosture = computeSopPosture(formValues as any, analysis ?? null);

    const classifyOptions = {
        strategyId,
        capturedForStrategy: latestPostureAnalysis.captured,
        strategyInput: {
            ...(sagittal ? { sagittal } : {}),
            sopCompletionRatio,
            sopTablePosture: { pattern: sopTablePosture.pattern },
        }
    };

    classifyPosture(latestPostureAnalysis.analysis as any, 'Unknown', classifyOptions)
        .then(setPostureClassification)
        .catch(err => {
            logger.error('Failed to calculate live SOP posture classification:', err);
            setPostureClassification(null);
        });

  }, [latestPostureAnalysis, completedTests, totalTests, settings.postureOrientationStrategyId, formValues, analysis]);

  const handleOpenVisioMeter = (testKey: string, side: 'L' | 'R') => {
    setSelectedTest({ testKey, side });
    setIsVGonOpen(true);
  };

  useEffect(() => {
    if (!initialAssessmentData) return;
    const skipKeys = new Set(['id', 'date', 'type', 'createdAt', 'updatedAt', 'clientId']);
    const initialFormValues: Record<string, string | number | null> = {};
    Object.keys(initialAssessmentData).forEach((key) => {
      if (skipKeys.has(key)) return;
      const v = initialAssessmentData[key as keyof Assessment];
      if (v === null || typeof v === 'string' || typeof v === 'number') {
        initialFormValues[key] = v;
      }
    });
    setFormValues((prev) => ({ ...prev, ...initialFormValues }));
  }, [initialAssessmentData, setFormValues]);

  const handleSelectAssessment = (assessmentId: string) => {
    if (!client?.id) return;
    navigate(`/clients/${client.id}/assessment/${encodeURIComponent(assessmentId)}/edit`);
  };

  const handleNewAssessment = () => {
    if (!client?.id) return;
    resetForm();
    navigate(`/clients/${client.id}/assessment/new`);
  };

  const handleVGonConfirm = (testKey: string, side: 'L' | 'R', value: number, imageData?: string) => {
    const fieldId = `${side}_${testKey}`;
    handleInputChange(fieldId, String(value));
    if (imageData) {
      handleInputChange(`${fieldId}_image`, imageData);
    }
  };

  const handleChangeCreatedAt = async (newDate: Date) => {
    if (!initialAssessmentData?.id || !client?.id) return;
    await updateAssessment(client.id, initialAssessmentData.id, {
      date: newDate,
    });
  };

  if (clientsOnly) {
    return (
      <PageLayout title="SOP Assessment" compactContent>
        <ClientsOnlyAppWall />
      </PageLayout>
    );
  }

  if (isClientLoading && !editMode) {
    return (
      <PageLayout title="SOP Assessment" isLoading>
        <div className="py-8 text-center text-muted">Loading client...</div>
      </PageLayout>
    );
  }

  if (!client && !editMode) {
    return (
      <PageLayout title="SOP Assessment" subtitle="Analyze movement patterns from assessment values.">
        <Card className="max-w-lg">
          <CardHeader>
            <CardTitle>Select a Client to Begin</CardTitle>
            <CardDescription>Choose an existing client or create a new one first.</CardDescription>
          </CardHeader>
          <CardContent className="flex gap-3">
            <SelectClientDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <Button onClick={() => setIsDialogOpen(true)}>
                <UserPlus className="mr-2 h-4 w-4" />
                Select Existing Client
              </Button>
            </SelectClientDialog>
            <Button asChild variant="secondary">
              <Link to="/clients/new">Create New Client</Link>
            </Button>
          </CardContent>
        </Card>
      </PageLayout>
    );
  }

  const renderPostureView = () => {
    return (
        <ClientPostureOverview
            analysis={latestPostureAnalysis}
            classification={postureClassification}
            layout="sidebar"
            showIntegratedSopSummary={!!postureClassification || !!analysis}
            sopAnalyzerAnalysis={analysis}
        />
    );
  };

  return (
    <PageLayout
      title="SOP Assessment"
      subtitle="Record test values and review practitioner-facing analysis."
      compactContent
    >
      {sheetImport.enabled && (
        <>
          <SopSheetImportPasteListener enabled parseAndApply={sheetImport.parseAndApply} />
          <SopSheetImportHiddenFileInput fileInputRef={sheetImport.fileInputRef} onChange={sheetImport.handleFileChange} />
        </>
      )}
      <div className="flex flex-col gap-4">
        {/* Inline toolbar — sits directly above Assessment Values card, works on mobile and desktop */}
        <div
          data-testid="sop-toolbar"
          className="flex w-full min-w-0 flex-wrap items-center gap-2"
        >
          {/* Assessment selector */}
          <DropdownMenu>
            <DropdownMenuTrigger
              className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-bold ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-transparent hover:bg-fg/5"
              aria-label="Select assessment"
            >
              <LayoutDashboard className="w-4 h-4 sm:mr-2" />
              <span className="truncate hidden sm:inline max-w-[180px]">
                {currentAssessmentLabel ?? 'New Assessment'}
              </span>
              <ChevronDown className="ml-2 h-4 w-4 opacity-50 hidden sm:block" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 max-h-72 overflow-y-auto">
              <DropdownMenuItem onClick={handleNewAssessment}>
                <LayoutDashboard className="mr-2 h-4 w-4" />
                New Assessment
              </DropdownMenuItem>
              {priorAssessments.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="flex items-center gap-2">
                    <History className="h-4 w-4" />
                    Previous Assessments
                  </DropdownMenuLabel>
                  {assessmentsForDropdown.map((assessment) => (
                    <DropdownMenuItem
                      key={assessment.id}
                      onClick={() => handleSelectAssessment(assessment.id)}
                    >
                      {assessment.label}
                    </DropdownMenuItem>
                  ))}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="h-6 w-px bg-border mx-1 shrink-0" role="separator" aria-orientation="vertical" />

          {/* Passive / Active — full words at the 44px control height */}
          <div className="flex h-11 items-center overflow-hidden rounded-md border border-line bg-bg shadow-sm">
            <button
              type="button"
              className={cn(
                'flex h-full min-h-11 items-center justify-center px-3 text-sm font-bold transition-colors',
                testMode === 'passive'
                  ? 'bg-accent text-accent-fg'
                  : 'bg-transparent text-fg/70 hover:bg-fg/5'
              )}
              onClick={() => handleTestModeChange('passive')}
              aria-label="Switch to passive tests"
              aria-pressed={testMode === 'passive'}
            >
              Passive
            </button>
            <button
              type="button"
              className={cn(
                'flex h-full min-h-11 items-center justify-center px-3 text-sm font-bold transition-colors',
                testMode === 'active'
                  ? 'bg-accent text-accent-fg'
                  : 'bg-transparent text-fg/70 hover:bg-fg/5'
              )}
              onClick={() => handleTestModeChange('active')}
              aria-label="Switch to active tests"
              aria-pressed={testMode === 'active'}
            >
              Active
            </button>
          </div>
          {/* Hidden accessible select keeps aria-label "Tests" reachable for screen readers */}
          <label htmlFor="mode" className="sr-only">Tests</label>
          <select
            id="mode"
            className="sr-only"
            value={testMode}
            onChange={(e) => handleTestModeChange(e.target.value as 'passive' | 'active')}
          >
            <option value="passive">Passive</option>
            <option value="active">Active</option>
          </select>

          <div className="h-6 w-px bg-border mx-1 shrink-0" role="separator" aria-orientation="vertical" />

          {/* View tools — same slot as Compare / Analysis on posture */}
          <ToolbarButton isPrimary onClick={() => setIsVGonOpen(true)}>
            <Camera className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">VGon</span>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => {
              setInsightsTab('body-map');
              setIsInsightsOpen(true);
            }}
            aria-label="Open body map insights"
          >
            <User className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Body Map</span>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => {
              setInsightsTab('sop-table');
              setIsInsightsOpen(true);
            }}
          >
            <Table className="mr-2 h-4 w-4" />
            <span>SoP Table</span>
          </ToolbarButton>
          {sheetImport.enabled && (
            <SopSheetImportButton
              triggerFilePicker={sheetImport.triggerFilePicker}
              isParsing={sheetImport.isParsing}
              disabled={saveStatus === 'saving'}
            />
          )}

          <div className="h-6 w-px bg-border mx-1 shrink-0" role="separator" aria-orientation="vertical" />

          {/* Persistence — Reset then Save, matching posture analysis */}
          <ToolbarButton
            variant="destructive"
            onClick={() => setResetConfirmOpen(true)}
            aria-label="Reset assessment"
            title="Reset assessment"
            className="h-11 min-h-11 min-w-11"
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            <span>Reset</span>
          </ToolbarButton>
          <ToolbarButton
            isPrimary
            onClick={handleSave}
            aria-label={
              saveStatus === 'saving'
                ? 'Saving...'
                : saveStatus === 'saved'
                  ? (editMode ? 'Updated!' : 'Saved!')
                  : (editMode ? 'Update' : 'Save')
            }
            title={editMode ? 'Update' : 'Save'}
            className={cn('h-11 min-h-11 min-w-11', saveStatus === 'saved' ? 'bg-status-clear hover:bg-status-clear/90' : '')}
            disabled={saveStatus === 'saving'}
          >
            <Save className="mr-2 h-4 w-4" />
            <span>
              {saveStatus === 'saving'
                ? 'Saving...'
                : saveStatus === 'saved'
                  ? (editMode ? 'Updated!' : 'Saved!')
                  : (editMode ? 'Update' : 'Save')}
            </span>
          </ToolbarButton>

          <ToolbarButton
            onClick={() => setShowOptionalAssessments((prev) => !prev)}
            className={cn(
              'h-11 min-h-11',
              showOptionalAssessments ? 'bg-accent text-accent-fg' : ''
            )}
            aria-label="Toggle optional assessments"
            aria-pressed={showOptionalAssessments}
          >
            Optional
          </ToolbarButton>
          <ToolbarButton
            onClick={handleExportPdf}
            aria-label="Export PDF"
            title="Export PDF"
            className="h-11 min-h-11 min-w-11"
          >
            <Download className="mr-2 h-4 w-4" />
            <span>PDF</span>
          </ToolbarButton>
        </div>

        {ruleDocVersion === 'legacy' && (
          <div className="rounded-md border border-status-warn bg-status-warn-surface p-3 text-sm text-status-warn">
            Legacy SoP mode is active (pre-March 2026 RuleDocs and table behavior).
          </div>
        )}
        {analysisError && (
          <div className="rounded-md border border-status-deviation bg-status-deviation-surface p-3 text-sm text-status-deviation">
            {analysisError}
          </div>
        )}

        <div className="flex flex-col xl:flex-row gap-6 items-start">
          <div className="flex-1 w-full overflow-hidden">
            <div data-testid="assessment-values-header">
              <TestInputGrid
                formValues={formValues}
                onInputChange={handleInputChange}
                onOpenVisioMeter={handleOpenVisioMeter}
                testDataMap={currentTestDataMap}
                layoutOrder={layoutOrder}
                showOptionalAssessments={showOptionalAssessments}
              />
            </div>
          </div>
        {!postureSidebarCollapsed ? (
          <div className="w-full xl:w-1/3 shrink-0 xl:sticky xl:top-[80px] transition-all duration-300">
            <div className="relative group">
              <button
                onClick={() => setPostureSidebarCollapsed(true)}
                className="absolute -left-3 top-4 h-6 w-6 rounded-full border bg-bg shadow-sm z-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-accent"
                title="Collapse Posture View"
              >
                <ChevronRight className="h-3 w-3" />
              </button>
              {renderPostureView()}
            </div>
          </div>
        ) : (
          <div className="hidden xl:flex xl:w-10 shrink-0 xl:sticky xl:top-[80px] flex-col items-center pt-4 border-l border-dashed border-line/50 h-[calc(100vh-120px)] transition-all duration-300">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 rounded-full border bg-bg shadow-sm hover:bg-accent/10 hover:text-accent transition-colors"
              onClick={() => setPostureSidebarCollapsed(false)}
              title="Expand Posture View"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="mt-8 flex flex-col items-center gap-4 py-4 cursor-pointer group" onClick={() => setPostureSidebarCollapsed(false)}>
              <div className="p-2 rounded-full bg-accent/5 text-accent group-hover:bg-accent/10 transition-colors">
                <PersonStanding className="h-5 w-5" />
              </div>
              <div className="[writing-mode:vertical-lr] text-[10px] font-bold text-muted/60 tracking-[0.2em] uppercase select-none group-hover:text-accent transition-colors">
                Posture Twin
              </div>
            </div>
          </div>
        )}
      </div>

      <SopAnalysisPanel
          analysis={analysis}
          isAnalyzing={isAnalyzing}
          completedTests={completedTests}
          totalTests={totalTests}
          formValues={formValues}
          experimentalFeaturesEnabled={!!settings.experimentalFeaturesEnabled}
          open={isInsightsOpen}
          onOpenChange={setIsInsightsOpen}
          activeTab={insightsTab}
          onTabChange={(tab) => setInsightsTab(tab as 'body-map' | 'sop-table')}
          visibleTestKeys={visibleTestKeys}
        />
      </div>

      <AlertDialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset this assessment?</AlertDialogTitle>
            <AlertDialogDescription>
              This clears the degrees you entered. You can cancel and keep them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                resetForm();
                setResetConfirmOpen(false);
              }}
            >
              Reset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <VGonSettingsProvider>
        <VisioMeter2
          isOpen={isVGonOpen}
          onClose={() => {
            setIsVGonOpen(false);
            setSelectedTest(null);
          }}
          formValues={formValues}
          onValueConfirm={handleVGonConfirm}
          initialTest={selectedTest ?? undefined}
          testDataMap={currentTestDataMap}
          clientName={client?.name}
        />
      </VGonSettingsProvider>
    </PageLayout>
  );
}
